import React, { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
type FitScoreRingProps = {
  score: number;
  size?: number;
  /** Re-runs the count-up whenever this key changes */
  runKey?: string | number;
  label?: string;
};

/** Animated thesis Fit Score dial. */
export function FitScoreRing({
  score,
  size = 132,
  runKey,
  label = 'Fit Score'
}: FitScoreRingProps) {
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(reduceMotion ? score : 0);
  useEffect(() => {
    if (reduceMotion) {
      setValue(score);
      return;
    }
    setValue(0);
    let frame = 0;
    const start = performance.now();
    const duration = 1100;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(score * eased));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [score, runKey, reduceMotion]);
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);
  return <div className="relative shrink-0" style={{
    width: size,
    height: size
  }} role="img" aria-label={`${label}: ${score} out of 100`} data-id="element-255" data-mp="4jhqdmy8padp">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true" data-id="element-256" data-mp="3vqaouj1nevej">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#F0EEEA" strokeWidth="6" data-id="element-257" data-mp="380zm4w0qmqxr" />
        <circle cx="50" cy="50" r={radius} fill="none" stroke="#20165F" strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} data-id="element-258" data-mp="33251doj7msb0" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" aria-hidden="true" data-id="element-259" data-mp="wa48yns82lph">
        <span className="font-mono text-[32px] font-semibold leading-none tracking-[-0.04em] text-text-primary" data-id="element-260" data-mp="s9tvkq23ot0l">
          {value}
        </span>
        <span className="mt-1 text-[9px] uppercase tracking-[0.18em] text-text-tertiary" data-id="element-261" data-mp="3l3ahx4rk4v2o">/100 · {label}</span>
      </div>
    </div>;
}